'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Phone,
  Search,
  PhoneIncoming,
  PhoneOutgoing,
  Loader2,
  User,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useCdrs } from '@/hooks/voice/use-voice-cdrs';
import { getRemoteParty } from '@/hooks/voice/cdr-helpers';
import type { VoiceCdr } from '@/hooks/voice/types';

export type ContactSource = 'recentes' | 'clientes' | 'contatos';

// Referência ESTÁVEL: o useCdrs depende do objeto de filtro (refetch keyed por
// ele). Inline `{limit:30}` criaria um filtro novo a cada render → loop infinito.
const RECENTES_FILTER = { limit: 30 };

interface Props {
  source: ContactSource;
  disabled: boolean;
  /** Recebe o telefone CRU do contato — o pai normaliza (toDialableBr) e disca. */
  onPick: (phone: string, name?: string) => void;
}

export function WebphoneContacts({ source, disabled, onPick }: Props) {
  if (source === 'recentes') {
    return <RecentesList disabled={disabled} onPick={onPick} />;
  }
  // key={source} reseta o estado de busca ao trocar de aba.
  return <SearchList key={source} source={source} disabled={disabled} onPick={onPick} />;
}

// ---------- item visual reaproveitado ----------
function ContactRow({
  name,
  phone,
  sub,
  icon,
  disabled,
  onPick,
}: {
  name: string;
  phone: string;
  sub?: string;
  icon?: React.ReactNode;
  disabled: boolean;
  onPick: (phone: string, name?: string) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(phone, name)}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent text-left disabled:opacity-50 disabled:cursor-not-allowed group"
      title={`Ligar para ${name} (${phone})`}
    >
      <span className="shrink-0 text-muted-foreground">
        {icon ?? <User className="h-4 w-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm truncate">{name}</span>
        {(sub || phone !== name) && (
          <span className="block text-[11px] text-muted-foreground font-mono truncate">
            {phone}
            {sub ? ` · ${sub}` : ''}
          </span>
        )}
      </span>
      <Phone className="h-4 w-4 text-emerald-600 opacity-0 group-hover:opacity-100 shrink-0" />
    </button>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 py-8 text-center text-xs text-muted-foreground">{children}</p>
  );
}

// ---------- Recentes (CDR) ----------
function RecentesList({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick: (phone: string, name?: string) => void;
}) {
  const { data, loading } = useCdrs(RECENTES_FILTER);

  // Deduplica pela parte remota — redial rápido sem repetir o mesmo número.
  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: { name: string; phone: string; cdr: VoiceCdr }[] = [];
    for (const c of data) {
      const phone = (c.direction === 'inbound' ? c.src : c.dst) ?? '';
      if (!phone || seen.has(phone)) continue;
      seen.add(phone);
      out.push({ name: getRemoteParty(c), phone, cdr: c });
      if (out.length >= 20) break;
    }
    return out;
  }, [data]);

  // Resolve número → nome do cliente (formato canônico) no backend. Sem acesso
  // a clientes (403) ou erro: fica só o número, sem quebrar.
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const nums = [...new Set(items.map((i) => i.phone).filter(Boolean))];
    if (nums.length === 0) return;
    let cancelled = false;
    api
      .request<Record<string, string>>(
        `/customers/resolve-phones?numbers=${encodeURIComponent(nums.join(','))}`,
      )
      .then((r) => {
        if (!cancelled) setNames(r || {});
      })
      .catch(() => {
        /* sem permissão / erro → mantém só o número */
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyHint>Sem chamadas recentes.</EmptyHint>
      </div>
    );
  }
  return (
    <div className="h-full overflow-auto py-1">
      {items.map(({ name, phone, cdr }) => (
        <ContactRow
          key={cdr.id}
          name={names[phone] ?? name}
          phone={phone}
          disabled={disabled}
          onPick={onPick}
          icon={
            cdr.direction === 'inbound' ? (
              <PhoneIncoming className="h-4 w-4 text-emerald-500" />
            ) : (
              <PhoneOutgoing className="h-4 w-4 text-blue-500" />
            )
          }
        />
      ))}
    </div>
  );
}

// ---------- Clientes / Contatos (busca) ----------
interface SearchResult {
  name: string;
  phone: string;
  sub?: string;
}

function SearchList({
  source,
  disabled,
  onPick,
}: {
  source: 'clientes' | 'contatos';
  disabled: boolean;
  onPick: (phone: string, name?: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) {
      setResults([]);
      setErr(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const t = setTimeout(async () => {
      try {
        const mapped =
          source === 'clientes'
            ? await searchClientes(q)
            : await searchContatos(q);
        if (!cancelled) setResults(mapped);
      } catch (e) {
        if (!cancelled) {
          const status = (e as { status?: number }).status;
          setErr(
            status === 403
              ? 'Você não tem acesso a esta lista.'
              : 'Não foi possível buscar agora.',
          );
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [term, source]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={
              source === 'clientes'
                ? 'Buscar cliente (nome, CPF, telefone)'
                : 'Buscar contato (nome, telefone)'
            }
            className="w-full rounded-lg border border-input bg-background pl-8 pr-2 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto py-1">
        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {!loading && err && <EmptyHint>{err}</EmptyHint>}
        {!loading && !err && term.trim().length < 2 && (
          <EmptyHint>Digite ao menos 2 letras para buscar.</EmptyHint>
        )}
        {!loading && !err && term.trim().length >= 2 && results.length === 0 && (
          <EmptyHint>Nenhum resultado com telefone.</EmptyHint>
        )}
        {!loading &&
          results.map((r, i) => (
            <ContactRow
              key={`${r.phone}-${i}`}
              name={r.name}
              phone={r.phone}
              sub={r.sub}
              disabled={disabled}
              onPick={onPick}
            />
          ))}
      </div>
    </div>
  );
}

async function searchClientes(q: string): Promise<SearchResult[]> {
  const res = await api.request<{ rows?: Array<{ name: string; phones?: string[]; document?: string | null }> }>(
    `/customers?q=${encodeURIComponent(q)}&limit=20`,
  );
  return (res.rows ?? [])
    .map((c) => ({
      name: c.name,
      phone: (c.phones ?? []).find((p) => p && p.length > 5) ?? '',
      sub: c.document ?? undefined,
    }))
    .filter((r) => r.phone);
}

async function searchContatos(q: string): Promise<SearchResult[]> {
  const res = await api.request<
    { payload?: Array<{ name: string; phone_number?: string | null }> } | Array<{ name: string; phone_number?: string | null }>
  >(`/chat/contacts?q=${encodeURIComponent(q)}`);
  const list = Array.isArray(res) ? res : (res.payload ?? []);
  return list
    .map((c) => ({ name: c.name || 'Sem nome', phone: c.phone_number ?? '' }))
    .filter((r) => r.phone);
}
